import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { showCallDialog } from 'flow/actions/call-dialog-actions';
import { selectUserCountryCode } from 'flow/selectors/ExtensionSelectors';
import { addPhoneLogEntry } from 'flow/utils/browser-utils';
import {
    convertNumberToE164,
    getNumberFromTelCalltoUrl,
    getProtocolType,
} from 'flow/utils/call-utils';
import { electronListeners } from 'flow/utils/electron-utils';

/**
 * Places a call when the desktop app is asked to open a tel: or callto: link.
 */
const useTelLinkClickedListener = () => {
    const dispatch = useAppDispatch();
    const ownCountryCode = useAppSelector(selectUserCountryCode);

    // Read when a link arrives, so the listener is registered once and a change of country
    // code cannot leave a second one behind (AC-2136: one click placed two calls).
    const ownCountryCodeRef = useRef(ownCountryCode);
    useEffect(() => {
        ownCountryCodeRef.current = ownCountryCode;
    }, [ownCountryCode]);

    useEffect(() => {
        const onTelLinkClicked = (
            _event: Electron.IpcRendererEvent,
            telUrl: string,
        ) => {
            const protocol = getProtocolType(telUrl);
            addPhoneLogEntry(`${protocol} link clicked to make call`);

            let number;
            try {
                number = getNumberFromTelCalltoUrl(telUrl);
            } catch (error) {
                addPhoneLogEntry(
                    `${protocol} link not called, unreadable link`,
                    error,
                );
                return;
            }

            const callableNumber = convertNumberToE164(
                number,
                ownCountryCodeRef.current,
            );
            dispatch(showCallDialog(callableNumber));
        };

        electronListeners.registerTelLinkClickedListener(onTelLinkClicked);

        return () => {
            electronListeners.removeTelLinkClickedListener(onTelLinkClicked);
        };
    }, [dispatch]);
};

export default useTelLinkClickedListener;
